import { PaletteLab } from './screens/PaletteLab';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { hasSession } from './lib/api';
import { AppStateProvider, useAppState } from './hooks/useAppState';
import { ToastContainer } from './components/shared/Toast';
import { ForgeTransitionProvider } from './components/layout/ForgeTransition';
import { Landing } from './screens/Landing';
import { Auth } from './screens/Auth';
import { Assessment } from './screens/Assessment';
import { ModelExplainer } from './screens/ModelExplainer';
import { ResourceHub } from './screens/ResourceHub';
import { StatsInput } from './screens/StatsInput';
import { FirmSelection } from './screens/FirmSelection';
import { Prescription } from './screens/Prescription';
import { Dashboard } from './screens/Dashboard';
import { Checklist } from './screens/Checklist';
import { TransitionPreview } from './screens/TransitionPreview';
import { TransitionPreview2 } from './screens/TransitionPreview2';
import { TransitionPreview3 } from './screens/TransitionPreview3';
import { TransitionPreviewCombined } from './screens/TransitionPreviewCombined';
import { SiegePreview } from './screens/SiegePreview';
import { SiegeTitleIntegrationPreview } from './screens/SiegeTitleIntegrationPreview';
import { SkilltreePreview } from './screens/SkilltreePreview';
import { LandingShieldPreview } from './screens/LandingShieldPreview';
import { CrestPreview } from './screens/CrestPreview';
import { Hub } from './screens/Hub';
import { useSkin } from './hooks/useSkin';
import { Tracker } from './screens/Tracker';
import { LedgerChartsPreview } from './screens/LedgerChartsPreview';
import { MarathonPhasesPreview } from './screens/MarathonPhasesPreview';
import { SkinMosaic } from './screens/SkinMosaic';
import { CursorLab } from './screens/CursorLab';
import { FirmAdmin } from './screens/FirmAdmin';
import { AuthCallback } from './screens/AuthCallback';
import { MemberAdmin } from './screens/MemberAdmin';

/* Member areas need a signed-in member; a saved or shared link opened without one lands on the home page.
   Dev keeps the mock sign-in, whose session arrives a moment after navigating. */
function Members({ children }: { children: ReactNode }) {
  const { user } = useAppState();
  const signedIn = hasSession() || (import.meta.env.DEV && user !== null);
  return signedIn ? <>{children}</> : <Navigate to="/" replace />;
}

function App() {
  useSkin(); // applies the saved skin to <html> on every route
  return (
    <AppStateProvider>
      <Router>
        <ForgeTransitionProvider>
        <Routes>
          {/* Public */}
          <Route path="/" element={<Landing />} />
          <Route path="/auth" element={<Auth />} />

          {/* Hub — first stop after sign-in */}
          <Route path="/hub" element={<Members><Hub /></Members>} />
          <Route path="/tracker" element={<Members><Tracker /></Members>} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/admin/firms" element={<Members><FirmAdmin /></Members>} />
          <Route path="/admin/members" element={<Members><MemberAdmin /></Members>} />

          {/* Journey */}
          <Route path="/assess" element={<Members><Assessment /></Members>} />
          <Route path="/learn" element={<Members><ModelExplainer /></Members>} />
          <Route path="/resources" element={<Members><ResourceHub /></Members>} />
          <Route path="/stats/:mode" element={<Members><StatsInput /></Members>} />
          <Route path="/firm" element={<Members><FirmSelection /></Members>} />
          <Route path="/prescription" element={<Members><Prescription /></Members>} />

          {/* Returning user */}
          <Route path="/dashboard" element={<Members><Dashboard /></Members>} />
          <Route path="/checklist" element={<Members><Checklist /></Members>} />

          {/* Previews */}
          <Route path="/preview/transitions" element={<TransitionPreview />} />
          <Route path="/preview/transitions2" element={<TransitionPreview2 />} />
          <Route path="/preview/transitions3" element={<TransitionPreview3 />} />
          <Route path="/preview/combined" element={<TransitionPreviewCombined />} />
          <Route path="/preview/siege" element={<SiegePreview />} />
          <Route path="/preview/siege-title" element={<SiegeTitleIntegrationPreview />} />
          <Route path="/preview/skilltree" element={<SkilltreePreview />} />
          <Route path="/preview/landing-shields" element={<LandingShieldPreview />} />
          <Route path="/preview/crest" element={<CrestPreview />} />
          <Route path="/preview/ledger-charts" element={<LedgerChartsPreview />} />
          <Route path="/preview/marathon" element={<MarathonPhasesPreview />} />
          <Route path="/preview/skins" element={<SkinMosaic />} />
          <Route path="/preview/cursors" element={<CursorLab />} />
          <Route path="/preview/palettes" element={<PaletteLab />} />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </ForgeTransitionProvider>
      </Router>
      <ToastContainer />
    </AppStateProvider>
  );
}

export default App;
